import { createPortal } from 'react-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Spin } from 'antd'
import { Icon } from '@iconify/react'
import { DragDropContext } from 'react-beautiful-dnd'

import { showActionNotification } from '../../../../components/notification'

import BoardHeader from './components/BoardHeader'
import BoardEmptyState from './components/BoardEmptyState'
import BoardJourneyToolbar from './components/BoardJourneyToolbar'
import MapTopSection from './components/MapTopSection'
import ReleasesPane from './components/ReleasesPane'
import StoryMapCardDetailModal from './components/StoryMapCardDetailModal'
import StoryMapBoardFilters from './components/StoryMapBoardFilters'
import StoryMapBoardSearch from './components/StoryMapBoardSearch'
import StoryMapAgentModal from './components/StoryMapAgentModal'
import StoryMapDeleteConfirm from './components/StoryMapDeleteConfirm'
import filterStyles from './components/StoryMapBoardFilters.module.css'
import toolbarStyles from './components/StoryMapBoardToolbar.module.css'
import useHideChromeOnScroll from './hooks/useHideChromeOnScroll'
import useStoryMapBoard from './hooks/useStoryMapBoard'
import { buildStoryMapLinkCatalog } from '../DescriptionEditor/buildStoryMapLinkCatalog'
import styles from '../ProjectStoryMapBoard.module.css'

const STORY_MAP_DRAWER_HOST_ID = 'story-map-drawer-host'

const ProjectStoryMapBoard = ({
    board: initialBoard,
    projectId,
    onBack,
    initialDeepLink,
    onDeepLinkHandled,
    readOnly = false,
    canDelete = false,
}) => {
    const storyMapBoard = useStoryMapBoard(initialBoard, projectId, {
        initialDeepLink,
        onDeepLinkHandled,
        readOnly,
        canDelete,
    })
    const {
        board,
        activeDragType,
        draggingStoryReleaseId,
        storyDragOverCellKey,
        draggingStepJourneyId,
        stepDragPlaceholder,
        editingJourneyId,
        editingJourneyDraft,
        editingStepId,
        editingStepDraft,
        editingStoryId,
        editingStoryDraft,
        topHScrollRef,
        topPaneRef,
        bodyPaneRef,
        journeyInputRef,
        stepInputRef,
        storyInputRef,
        boardTracks,
        gridTemplateColumns,
        boardStats,
        commitBoardTitle,
        syncHorizontalScroll,
        updateJourneyDraft,
        updateStepDraft,
        updateStoryDraft,
        commitJourneyTitle,
        commitStepTitle,
        commitStoryTitle,
        insertJourneyAt,
        insertStepAt,
        addRelease,
        addStory,
        handleDragStart,
        handleDragUpdate,
        handleDragEnd,
        journeyMenu,
        stepMenu,
        storyMenu,
        releaseMenu,
        selectedCardDetail,
        highlightedCardId,
        handleCopyCardLink,
        handleCopyBoardLink,
        openStepDetail,
        openStoryDetail,
        openJourneyDetail,
        openReleaseDetail,
        closeCardDetail,
        handleCardDetailUpdate,
        handleBoardMouseDown,
        deleteConfirm,
        closeDeleteConfirm,
        missingDeepLinkCard,
        closeMissingDeepLinkCard,
        boardFilters,
        filterLoading,
        applyBoardFilters,
        filterMatchCount,
        filterMatchIndex,
        filterFocusedCardId,
        isFilterActive,
        goToNextFilterMatch,
        goToPrevFilterMatch,
        applyAgentGeneration,
        discardAgentDrafts,
        acceptAgentDrafts,
        savingAgentDrafts,
        agentDraftCounts,
    } = storyMapBoard
    const toggleReleaseCollapse = storyMapBoard.toggleReleaseCollapse
    useHideChromeOnScroll(bodyPaneRef)
    const handleAddComment = storyMapBoard.handleAddComment
    const handleDeleteComment = storyMapBoard.handleDeleteComment
    const handleEditComment = storyMapBoard.handleEditComment
    const handleLoadComments = storyMapBoard.handleLoadComments
    const handleLoadActivity = storyMapBoard.handleLoadActivity
    const handleLoadCardDetail = storyMapBoard.handleLoadCardDetail

    const handleNavigateCard = useCallback(
        (type, id) => {
            if (!id) return
            if (type === 'journey') openJourneyDetail(id)
            else if (type === 'step') openStepDetail(id)
            else if (type === 'story') openStoryDetail(id)
            else if (type === 'release') openReleaseDetail(id)
        },
        [openJourneyDetail, openReleaseDetail, openStepDetail, openStoryDetail]
    )

    const isBoardEmpty = board.journeys.length === 0
    const drawerOpen = Boolean(selectedCardDetail)
    const drawerRef = useRef(null)
    const cardDetailRef = useRef(null)
    const [drawerPortalTarget, setDrawerPortalTarget] = useState(null)

    const linkCatalog = useMemo(
        () => buildStoryMapLinkCatalog({ board, projectId }),
        [board, projectId]
    )

    useEffect(() => {
        const resolveHost = () =>
            document.getElementById(STORY_MAP_DRAWER_HOST_ID)

        setDrawerPortalTarget(resolveHost())

        if (resolveHost()) return undefined

        const timer = window.setTimeout(() => {
            setDrawerPortalTarget(resolveHost())
        }, 0)

        return () => window.clearTimeout(timer)
    }, [])

    useEffect(() => {
        const host = document.getElementById(STORY_MAP_DRAWER_HOST_ID)
        if (!host) return undefined

        host.style.pointerEvents = drawerOpen ? 'auto' : 'none'
        return () => {
            host.style.pointerEvents = 'none'
        }
    }, [drawerOpen])

    const requestCloseCardDetail = useCallback(() => {
        cardDetailRef.current?.flushPendingEdits?.()
        closeCardDetail()
    }, [closeCardDetail])

    const handleDrawerClickOutside = useCallback(
        (event) => {
            if (!drawerOpen || !drawerRef.current) return

            const composedPath = event.composedPath ? event.composedPath() : []
            const isInsideDrawer =
                drawerRef.current.contains(event.target) ||
                composedPath.includes(drawerRef.current)

            if (!isInsideDrawer) {
                const isModal = event.target.closest?.('.ant-modal')
                const isPopover = event.target.closest?.('.ant-popover')
                const isDropdown = event.target.closest?.('.ant-dropdown')
                const isDeleteConfirm = event.target.closest?.(
                    '[data-story-map-confirm-overlay]'
                )
                const isAppLinkPicker = event.target.closest?.(
                    '[data-app-link-picker-overlay]'
                )
                const isActivityModal = event.target.closest?.(
                    '[data-story-map-activity-overlay]'
                )

                if (
                    !isModal &&
                    !isPopover &&
                    !isDropdown &&
                    !isDeleteConfirm &&
                    !isAppLinkPicker &&
                    !isActivityModal
                ) {
                    requestCloseCardDetail()
                }
            }
        },
        [drawerOpen, requestCloseCardDetail]
    )

    useEffect(() => {
        if (!drawerOpen) return undefined

        document.addEventListener('mousedown', handleDrawerClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleDrawerClickOutside)
        }
    }, [drawerOpen, handleDrawerClickOutside])

    const boardHeader = (
        <BoardHeader
            title={board.title}
            boardStats={boardStats}
            onBack={onBack}
            onCommitTitle={commitBoardTitle}
            onCopyLink={handleCopyBoardLink}
            variant="default"
            readOnly={readOnly}
        />
    )

    const handleBoardSearchChange = useCallback(
        (search) => {
            applyBoardFilters({
                ...boardFilters,
                search,
            })
        },
        [applyBoardFilters, boardFilters]
    )

    // ---- Storymap Agent ------------------------------------------------------
    const [agentModalOpen, setAgentModalOpen] = useState(false)
    const hasAgentDrafts = (agentDraftCounts?.total ?? 0) > 0
    // Draft counts accumulated across one generation run: streamed journeys
    // merge incrementally, so the final result's own merge summary only covers
    // the remainder — the notification needs the running total.
    const agentRunTotalsRef = useRef({ journeys: 0, steps: 0, stories: 0 })

    const addAgentRunSummary = useCallback((summary) => {
        agentRunTotalsRef.current = {
            journeys: agentRunTotalsRef.current.journeys + (summary?.journeys ?? 0),
            steps: agentRunTotalsRef.current.steps + (summary?.steps ?? 0),
            stories: agentRunTotalsRef.current.stories + (summary?.stories ?? 0),
        }
    }, [])

    const openAgentModal = useCallback(() => {
        agentRunTotalsRef.current = { journeys: 0, steps: 0, stories: 0 }
        setAgentModalOpen(true)
    }, [])

    // One streamed journey (update mode): merge it as draft cards right away —
    // the id diff inside applyAgentGeneration keeps repeat merges duplicate-free.
    const handleAgentPartial = useCallback(
        (event) => {
            if (event?.kind !== 'journey' || !event.journey) return
            const { summary } = applyAgentGeneration({
                story_map: {
                    board: {
                        journeys: [event.journey],
                        release: [
                            {
                                _id: event.release_id,
                                stories: event.stories ?? [],
                            },
                        ],
                    },
                },
            })
            addAgentRunSummary(summary)
        },
        [addAgentRunSummary, applyAgentGeneration]
    )

    const handleAgentGenerated = useCallback(
        (aiResult) => {
            setAgentModalOpen(false)
            const { success, summary } = applyAgentGeneration(aiResult)
            if (!success) {
                showActionNotification({
                    type: 'error',
                    message: 'The agent returned an unexpected result',
                })
                return
            }
            addAgentRunSummary(summary)
            const totals = agentRunTotalsRef.current
            const total = totals.journeys + totals.steps + totals.stories
            if (total === 0) {
                showActionNotification({
                    type: 'info',
                    message:
                        'The transcripts did not produce any new cards for this board',
                })
                return
            }
            showActionNotification({
                type: 'success',
                message: `AI draft ready — ${totals.stories} stories, ${totals.steps} steps, ${totals.journeys} journeys`,
                subMessage: 'Review the dashed cards, then save or discard the draft',
            })
        },
        [addAgentRunSummary, applyAgentGeneration]
    )

    const boardToolbar = (
        <>
            <StoryMapBoardFilters
                filters={boardFilters}
                onChange={applyBoardFilters}
                loading={filterLoading}
                isFilterActive={isFilterActive}
                filterMatchCount={filterMatchCount}
                filterMatchIndex={filterMatchIndex}
                onPrevMatch={goToPrevFilterMatch}
                onNextMatch={goToNextFilterMatch}
                variant="tabBar"
            />
            <span className={toolbarStyles.toolbarDivider} aria-hidden />
            <StoryMapBoardSearch
                value={boardFilters?.search ?? ''}
                onChange={handleBoardSearchChange}
            />
            {!readOnly ? (
                <>
                    <span className={toolbarStyles.toolbarDivider} aria-hidden />
                    <button
                        type="button"
                        className={toolbarStyles.searchIconButton}
                        title="Storymap Agent"
                        aria-label="Storymap Agent"
                        onClick={openAgentModal}
                    >
                        <Icon
                            icon="mdi:robot-outline"
                            width={19}
                            height={19}
                            style={{ color: '#6366f1' }}
                        />
                    </button>
                </>
            ) : null}
        </>
    )

    const agentDraftBanner = hasAgentDrafts ? (
        <div className={styles.agentDraftBanner}>
            <Icon
                icon="mdi:sparkles"
                width={16}
                height={16}
                style={{ color: '#6366f1', flexShrink: 0 }}
            />
            <span className={styles.agentDraftBannerText}>
                AI draft — {agentDraftCounts.stories} stories,{' '}
                {agentDraftCounts.steps} steps, {agentDraftCounts.journeys}{' '}
                journeys pending review
            </span>
            <Button
                size="small"
                disabled={savingAgentDrafts}
                onClick={discardAgentDrafts}
            >
                Discard
            </Button>
            <Button
                size="small"
                type="primary"
                style={{ background: '#6366f1' }}
                loading={savingAgentDrafts}
                onClick={acceptAgentDrafts}
            >
                Save to board
            </Button>
        </div>
    ) : null

    const drawerElement = drawerOpen ? (
        <div
            ref={drawerRef}
            className={`${styles.cardDetailDrawer} ${
                drawerPortalTarget ? styles.cardDetailDrawerPortaled : ''
            } ${drawerOpen ? styles.cardDetailDrawerOpen : ''}`}
            aria-hidden={!drawerOpen}
        >
            <StoryMapCardDetailModal
                ref={cardDetailRef}
                open={drawerOpen}
                projectId={projectId}
                cardType={selectedCardDetail?.type}
                card={selectedCardDetail?.card}
                context={selectedCardDetail?.context}
                linkCatalog={linkCatalog}
                onClose={closeCardDetail}
                onUpdate={handleCardDetailUpdate}
                onCopyCardLink={handleCopyCardLink}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onEditComment={handleEditComment}
                onLoadComments={handleLoadComments}
                onLoadActivity={handleLoadActivity}
                onLoadCardDetail={handleLoadCardDetail}
                onNavigateCard={handleNavigateCard}
                readOnly={readOnly}
                canDelete={canDelete}
            />
        </div>
    ) : null

    return (
        <>
        <div
            className={`${styles.boardPage} ${
                drawerOpen ? styles.boardPageDrawerOpen : ''
            }`}
        >
            <div className={styles.boardInlineHeaderWrap}>
                {boardHeader}
                <div className={styles.boardTabBarActions}>{boardToolbar}</div>
            </div>

            {agentDraftBanner}

                <div
                    className={`${styles.boardShell} ${
                        drawerOpen ? styles.boardShellDrawerOpen : ''
                    } ${
                        activeDragType === 'STORY' || activeDragType === 'STEP'
                            ? styles.boardShellDragging
                            : ''
                    }`}
                >
                <div
                    className={`${styles.boardMain} ${
                        drawerOpen ? styles.boardMainDrawerOpen : ''
                    }`}
                >
                    <div
                        className={`${styles.boardBody} ${
                            filterLoading ? filterStyles.boardBodyFiltered : ''
                        }`}
                    >
                        {filterLoading ? (
                            <div className={filterStyles.loadingOverlay} aria-hidden>
                                <Spin />
                            </div>
                        ) : null}
                        <DragDropContext
                            onDragStart={handleDragStart}
                            onDragUpdate={handleDragUpdate}
                            onDragEnd={handleDragEnd}
                        >
                            <div
                                className={`${styles.mapCanvas} ${
                                    isBoardEmpty ? styles.mapCanvasEmpty : ''
                                } ${
                                    board.isFilterActive ? styles.mapCanvasFilterActive : ''
                                } ${
                                    activeDragType === 'STORY'
                                        ? styles.mapCanvasStoryDragging
                                        : ''
                                } ${
                                    activeDragType === 'RELEASE'
                                        ? styles.mapCanvasReleaseDragging
                                        : ''
                                } ${
                                    activeDragType === 'STEP'
                                        ? styles.mapCanvasStepDragging
                                        : ''
                                }`}
                                onMouseDownCapture={handleBoardMouseDown}
                            >
                                <div className={styles.mapTopPane} ref={topPaneRef}>
                                    {isBoardEmpty ? (
                                        readOnly ? null : (
                                            <BoardJourneyToolbar
                                                onAddJourney={() => insertJourneyAt(0)}
                                            />
                                        )
                                    ) : (
                                        <div
                                            className={`${styles.mapHScroll} ${styles.mapHScrollTop}`}
                                            ref={topHScrollRef}
                                            onScroll={(event) =>
                                                syncHorizontalScroll(event.currentTarget)
                                            }
                                        >
                                            <div className={styles.mapWide}>
                                                <MapTopSection
                                                    boardTracks={boardTracks}
                                                    gridTemplateColumns={
                                                        gridTemplateColumns
                                                    }
                                                    journeys={board.journeys}
                                                    editingJourneyId={editingJourneyId}
                                                    editingJourneyDraft={
                                                        editingJourneyDraft
                                                    }
                                                    journeyInputRef={journeyInputRef}
                                                    journeyMenu={journeyMenu}
                                                    onDraftChange={updateJourneyDraft}
                                                    onCommitJourneyTitle={
                                                        commitJourneyTitle
                                                    }
                                                    onInsertJourneyAt={insertJourneyAt}
                                                    onInsertStepAt={insertStepAt}
                                                    onOpenJourney={openJourneyDetail}
                                                    onOpenStep={openStepDetail}
                                                    editingStepId={editingStepId}
                                                    editingStepDraft={editingStepDraft}
                                                    stepInputRef={stepInputRef}
                                                    onStepDraftChange={updateStepDraft}
                                                    onCommitStepTitle={commitStepTitle}
                                                    draggingStepJourneyId={
                                                        draggingStepJourneyId
                                                    }
                                                    stepDragPlaceholder={
                                                        stepDragPlaceholder
                                                    }
                                                    stepMenu={stepMenu}
                                                    highlightedCardId={highlightedCardId}
                                                    isFilterActive={board.isFilterActive}
                                                    filterFocusedCardId={filterFocusedCardId}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`${styles.mapBodyPane} ${
                                        isBoardEmpty ? styles.mapBodyPaneEmpty : ''
                                    }`}
                                    ref={bodyPaneRef}
                                >
                                    {isBoardEmpty ? (
                                        <BoardEmptyState readOnly={readOnly} />
                                    ) : (
                                        <ReleasesPane
                                            releases={board.releases}
                                            gridTemplateColumns={gridTemplateColumns}
                                            boardTracks={boardTracks}
                                            draggingStoryReleaseId={
                                                draggingStoryReleaseId
                                            }
                                            storyDragOverCellKey={storyDragOverCellKey}
                                            isStoryDragging={activeDragType === 'STORY'}
                                            onSyncHorizontalScroll={
                                                syncHorizontalScroll
                                            }
                                            onAddRelease={addRelease}
                                            onAddStory={addStory}
                                            onToggleReleaseCollapse={
                                                toggleReleaseCollapse
                                            }
                                            onOpenStory={openStoryDetail}
                                            onOpenRelease={openReleaseDetail}
                                            releaseMenu={releaseMenu}
                                            editingStoryId={editingStoryId}
                                            editingStoryDraft={editingStoryDraft}
                                            storyInputRef={storyInputRef}
                                            onStoryDraftChange={updateStoryDraft}
                                            onCommitStoryTitle={commitStoryTitle}
                                            storyMenu={storyMenu}
                                            highlightedCardId={highlightedCardId}
                                            isFilterActive={board.isFilterActive}
                                            filterFocusedCardId={filterFocusedCardId}
                                            readOnly={readOnly}
                                        />
                                    )}
                                </div>
                            </div>
                        </DragDropContext>
                    </div>
                </div>
            </div>
        </div>

        {drawerElement && drawerPortalTarget
            ? createPortal(drawerElement, drawerPortalTarget)
            : null}

        <StoryMapAgentModal
            open={agentModalOpen}
            onCancel={() => setAgentModalOpen(false)}
            storyMapId={board.id}
            onPartial={handleAgentPartial}
            onGenerated={handleAgentGenerated}
        />

        <StoryMapDeleteConfirm
            open={Boolean(deleteConfirm)}
            title={deleteConfirm?.title}
            message={deleteConfirm?.message}
            detail={deleteConfirm?.detail}
            preview={deleteConfirm?.preview}
            previewIcon={deleteConfirm?.previewIcon}
            onCancel={closeDeleteConfirm}
            onConfirm={deleteConfirm?.onConfirm}
        />

        <StoryMapDeleteConfirm
            open={Boolean(missingDeepLinkCard)}
            alertMode
            title={
                missingDeepLinkCard?.type === 'step'
                    ? 'Step not found'
                    : 'Story not found'
            }
            message={
                missingDeepLinkCard?.type === 'step'
                    ? 'This step has been deleted or is no longer available on this story map.'
                    : 'This story has been deleted or is no longer available on this story map.'
            }
            previewIcon="mdi:link-variant-off"
            confirmText="OK"
            onCancel={closeMissingDeepLinkCard}
        />
        </>
    )
}

export default ProjectStoryMapBoard
